import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mic, Languages, Globe } from "lucide-react";

export default function Media() {
  const { org } = useAuth();
  const [tiers, setTiers] = useState(null);
  const [accents, setAccents] = useState([]);
  const [baseline, setBaseline] = useState(null);
  const [welsh, setWelsh] = useState(false);
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");

  useEffect(() => {
    api.get("/media/languages/tiers").then((r) => setTiers(r.data)).catch(() => {});
    api.get("/media/accents").then((r) => setAccents(r.data.accents || r.data || [])).catch(() => {});
    api.get("/media/regions/baseline").then((r) => setBaseline(r.data)).catch(() => {});
    if (org?.id) api.get(`/orgs/${org.id}/projects`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.projects || []);
      setProjects(list); if (list[0]) setPid(list[0].id);
    }).catch(() => {});
  }, [org]);

  const toggleWelsh = async () => {
    if (!pid) { toast.error("Pick a project first"); return; }
    const next = !welsh;
    setWelsh(next);
    try { await api.post("/media/languages/welsh-toggle", { project_id: pid, enabled: next }); toast.success(`Welsh ${next ? "enabled" : "disabled"}`); }
    catch (e) { setWelsh(!next); toast.error(errMsg(e)); }
  };

  const tierGroups = tiers ? [
    { label: "Tier 1 · machine-translated (review before use)", items: tiers.tier1 || [] },
    { label: "Tier 2 · reviewed batches", items: tiers.tier2 || [] },
    { label: "Tier 3 · reviewed batches", items: tiers.tier3 || [] },
  ].filter((g) => Array.isArray(g.items) && g.items.length) : [];
  const langName = (x) => (typeof x === "string" ? x : (x.name || x.language || x.code || ""));

  return (
    <Shell>
      <h1 className="k-heading" style={{ fontSize: 30, margin: 0 }}>Media &amp; Languages</h1>

      <div className="k-container" style={{ padding: 20, marginTop: 16 }} data-testid="media-tiers">
        <h3 className="k-heading" style={{ fontSize: 16, marginTop: 0 }}><Languages size={15} /> Language tiers</h3>
        {tierGroups.length ? tierGroups.map((g) => (
          <div key={g.label} style={{ marginBottom: 10 }}>
            <div className="k-muted" style={{ fontSize: 12, marginBottom: 4 }}>{g.label}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {g.items.map((t, i) => <span key={i} className="k-badge k-badge-sandbox">{langName(t)}</span>)}
            </div>
          </div>
        )) : <p className="k-muted" style={{ fontSize: 12 }}>{tiers ? "No tiers configured." : "Loading…"}</p>}
        {tiers?.label && <p className="k-muted" style={{ fontSize: 11.5, marginTop: 4 }}>{tiers.label}</p>}
      </div>

      <div className="k-container" style={{ padding: 20, marginTop: 16 }} data-testid="media-accents">
        <h3 className="k-heading" style={{ fontSize: 16, marginTop: 0 }}><Mic size={15} /> Accents benchmarked</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(accents || []).map((a, i) => <span key={i} className="k-badge">{typeof a === "string" ? a : (a.accent || a.name)}</span>)}
          {(!accents || accents.length === 0) && <span className="k-muted" style={{ fontSize: 12 }}>None yet.</span>}
        </div>
      </div>

      {baseline && (
        <div className="k-container" style={{ padding: 20, marginTop: 16 }} data-testid="media-baseline">
          <h3 className="k-heading" style={{ fontSize: 16, marginTop: 0 }}><Globe size={15} /> Baseline regions</h3>
          <p className="k-muted" style={{ fontSize: 12 }}>{baseline.note || (baseline.regions || []).join(", ")}</p>
        </div>
      )}

      <div className="k-container" style={{ padding: 20, marginTop: 16 }} data-testid="media-welsh">
        <label className="k-muted" style={{ fontSize: 12 }}>Project</label>
        <select className="k-control" data-testid="media-project-select" value={pid} onChange={(e) => setPid(e.target.value)} style={{ marginTop: 4, marginBottom: 12 }}>
          {projects.length === 0 && <option value="">No projects yet</option>}
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button className={welsh ? "k-btn k-btn-primary" : "k-btn k-btn-ghost"} data-testid="media-welsh-toggle" onClick={toggleWelsh}>
          Welsh-language support: {welsh ? "On" : "Off"}
        </button>
        <p className="k-muted" style={{ fontSize: 11.5, marginTop: 8 }}>Welsh Language Standards support for public-facing Welsh services.</p>
      </div>
    </Shell>
  );
}
